import './../../scss/components/_icons.scss';

import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { specialistClientService } from '../../implementation-specialist/services/implementation-client-service';
import { RootState } from '../state/root-reducer';

import CustomizeableAccordion from './customizeable-accordion';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    [theme.breakpoints.down('lg')]: {
      flexDirection: 'column',
      alignItems: 'center'
    },
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    }
  },
}));

interface footerProps {
  isMobile?: boolean
}

function FppFooter(props: footerProps) {
  const { isMobile } = props;
  const classes = useStyles();
  const year = new Date().getFullYear();

  const selectedEncounter = useSelector(
    (state: RootState) => state.patientContext.selectedEncounter
  );
  const clientId =
    selectedEncounter?.clientId && selectedEncounter.clientId > 0
      ? selectedEncounter.clientId
      : null;

  const [supportPhone, setSupportPhone] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const fetchedClientIdRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    const clearContact = () => {
      setSupportPhone('');
      setSupportEmail('');
      fetchedClientIdRef.current = null;
    };

    if (!clientId) {
      clearContact();
      return;
    }

    if (fetchedClientIdRef.current === clientId) {
      return;
    }

    // Avoid showing prior encounter's contact while the next client loads
    setSupportPhone('');
    setSupportEmail('');

    const fetchClientSupport = async () => {
      const response = await specialistClientService.getClient(clientId);
      if (cancelled) {
        return;
      }
      if (response?.hasErrors || !response?.entity?.fullClient) {
        clearContact();
        return;
      }

      const fullClient = response.entity.fullClient;
      const phone = (fullClient.supportPhone ?? fullClient.support_phone ?? '').toString().trim();
      const email = (fullClient.supportEmail ?? fullClient.support_email ?? '').toString().trim();
      fetchedClientIdRef.current = clientId;
      setSupportPhone(phone);
      setSupportEmail(email);
    };

    fetchClientSupport();

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const hasPhone = Boolean(supportPhone);
  const hasEmail = Boolean(supportEmail);
  const hasContact = hasPhone || hasEmail;

  const contents = (
    <Grid container justifyContent="center" alignItems="center" className="py-4 px-6">
      <Grid container spacing={2} justifyContent="center" className="icon-colors">
        {hasContact && (
          <Grid item xs={12}>
            <Grid container justifyContent="space-between" spacing={2} className={classes.root} >
              {hasPhone && (
                <Grid item>
                  <Box display="flex" alignItems="center">
                    <PhoneIcon className="icon" fontSize="large" />
                    <Typography variant="body1" className="pl-1">
                      {supportPhone}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {hasEmail && (
                <Grid item>
                  <Box display="flex">
                    <EmailIcon className="icon" />
                    <Typography variant="body1" className="pl-2 pr-8">
                      {supportEmail}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Grid>
        )}
        {hasContact && (
          <Grid item xs={12}>
            <Divider />
          </Grid>
        )}
        <Grid item xs={12}>
          <Grid container justifyContent="center" spacing={2} className={classes.root} >
            <Grid item>
              <Typography variant="body2">
                {`FinPay, All Rights Reserved © ${year}`}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )

  return (
    <Paper elevation={3}>
      {isMobile ? (
        <CustomizeableAccordion
          headerText="Support Contact Information"
          details={contents}
          />
        ) : (
          contents
        )
      }
    </Paper>
  );
}

export default FppFooter;
